import type { AxiosProgressEvent } from 'axios';
import { type AxiosRequestConfig } from 'axios';
import qs from 'qs';
import { apiClient } from './api-client';

export type TOptions = Omit<
  AxiosRequestConfig,
  'data' | 'onDownloadProgress' | 'onUploadProgress'
> & {
  withOrg?: boolean;
  /**
   * Reject with the raw `AxiosError` rather than the flattened message string,
   * so the caller can read the server's structured body (see
   * `responseErrorInterceptor`). Opt-in — every existing caller keeps the
   * string it already handles.
   */
  preserveError?: boolean;
  body?: AxiosRequestConfig['data'];
  onDownloadProgress?: (progress: number) => void;
  onUploadProgress?: (progress: number) => void;
};

const onProgress = (
  progressEvent: AxiosProgressEvent,
  cb: (percent: number) => void
) => {
  const percent = Math.round(
    (progressEvent.loaded * 100) / (progressEvent.total || 1)
  );
  cb(percent);
};

/**
 * A caller-built URLSearchParams (e.g. inbox's `toSearchParams`) already has
 * the exact repeated-key wire format it wants; handing it to `qs.stringify`
 * instead reads none of its entries and serializes to `''`, silently dropping
 * every param. Axios's own default would do the right thing here, but
 * defining `paramsSerializer` at all opts out of that default for every
 * request, so the check has to be explicit. Exported (not inlined below) so
 * a regression here — someone deleting the branch during a cleanup — fails a
 * test instead of failing silently in production.
 */
export function serializeRequestParams(params: unknown): string {
  return params instanceof URLSearchParams
    ? params.toString()
    : qs.stringify(params, {
        allowEmptyArrays: true,
        skipNulls: true,
        arrayFormat: 'repeat',
      });
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const request = async <T>({
  withOrg = true,
  body,
  onDownloadProgress,
  onUploadProgress,
  ...options
}: TOptions) => {
  const isFormData = body instanceof FormData;

  const config: AxiosRequestConfig & {
    withOrg?: boolean;
    preserveError?: boolean;
  } = {
    ...options,
    method: options.method ?? 'GET',
    data: body,
    withOrg,
    headers: {
      ...(isFormData
        ? { 'Content-Type': 'multipart/form-data' }
        : { 'Content-Type': 'application/json' }),
      ...options.headers,
    },

    ...(onUploadProgress && {
      onUploadProgress: (event) => onProgress(event, onUploadProgress),
    }),

    ...(onDownloadProgress && {
      onDownloadProgress: (event) => onProgress(event, onDownloadProgress),
    }),

    paramsSerializer: {
      serialize: serializeRequestParams,
    },

    responseType: options.responseType ?? 'json',
  };

  const response = await apiClient(config);

  return response.data;
};
